import {
    ButtonGradientPrimary,
    Colors,
    FIXED_ICON_SIZES,
    Icon,
    Pagination,
    type OptionProps,
} from '@amelio/ui-library';

import type { CycleEvaluation } from '@/types/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';

import { useAmelioTranslations } from '@/hooks';
import { CycleEvaluationGroupRow } from '@/pages/CycleEvaluation/Dashboard/components/CycleEvaluationGroupRow/CycleEvaluationGroupRow';
import { CycleEvaluationRowWithResolvedTitle } from '@/pages/CycleEvaluation/Dashboard/components/CycleEvaluationRowWithResolvedTitle/CycleEvaluationRowWithResolvedTitle';
import { CYCLE_LIST_PAGE_SIZE } from '@/pages/CycleEvaluation/Dashboard/cycleListRows';
import { cycleCreateButtonHeight } from '@/utils/cycleStyles';
import { lastPageOf } from '@/utils/pagination';

export interface CycleEvaluationTemplateListProps {
    cycleEvaluationGroups: CycleEvaluationGroup[];
    cycleEvaluations: CycleEvaluation[];
    /** Unfiltered evaluation list, used to resolve the template names of each cycle's children. */
    allCycleEvaluations?: CycleEvaluation[];
    actionMenuItems: (cycleEvaluation: CycleEvaluation) => OptionProps[];
    groupActionMenuItems: (group: CycleEvaluationGroup) => OptionProps[];
    isUpdating: boolean;
    currentEvaluation?: CycleEvaluation;
    isRefetching: boolean;
    onCreateCycle: () => void;
    onEditEvaluation: (cycleEvaluation: CycleEvaluation) => void;
    onEditGroup: (group: CycleEvaluationGroup) => void;
    currentPage?: number;
    totalCount?: number;
    onPageChange?: (page: number) => void;
}

export const CycleEvaluationTemplateList = ({
    cycleEvaluationGroups = [] as CycleEvaluationGroup[],
    cycleEvaluations = [] as CycleEvaluation[],
    allCycleEvaluations,
    actionMenuItems,
    groupActionMenuItems,
    isUpdating,
    currentEvaluation,
    isRefetching,
    onCreateCycle,
    onEditEvaluation,
    onEditGroup,
    currentPage = 1,
    totalCount = cycleEvaluationGroups.length + cycleEvaluations.length,
    onPageChange = () => {},
}: CycleEvaluationTemplateListProps) => {
    const { t } = useAmelioTranslations();
    const totalPages = lastPageOf(totalCount, CYCLE_LIST_PAGE_SIZE);
    const resultsRangeStart = totalCount === 0 ? 0 : (currentPage - 1) * CYCLE_LIST_PAGE_SIZE + 1;
    const resultsRangeEnd = Math.min(currentPage * CYCLE_LIST_PAGE_SIZE, totalCount);

    return (
        <div
            className={'flex flex-col max-md:gap-[16px] md:gap-[24px]'}
            data-testid={'cycle-evaluation-list'}
        >
            {cycleEvaluationGroups.map((group) => (
                <CycleEvaluationGroupRow
                    key={group.id}
                    group={group}
                    actionMenuItems={groupActionMenuItems}
                    allCycleEvaluations={allCycleEvaluations}
                    onRowClick={() => onEditGroup(group)}
                />
            ))}

            {cycleEvaluations.map((cycleEvaluation) => (
                <CycleEvaluationRowWithResolvedTitle
                    key={cycleEvaluation.id}
                    cycleEvaluation={cycleEvaluation}
                    actionMenuItems={actionMenuItems}
                    currentEvaluation={currentEvaluation}
                    isUpdating={isUpdating}
                    isRefetching={isRefetching}
                    onRowClick={() => onEditEvaluation(cycleEvaluation)}
                />
            ))}

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={CYCLE_LIST_PAGE_SIZE}
                onPageChange={onPageChange}
                isLoading={isRefetching}
                dataTestId={'cycle-evaluation-list-pagination'}
                resultsSummaryText={t('common.showingResults', {
                    start: resultsRangeStart,
                    end: resultsRangeEnd,
                    total: totalCount,
                })}
            />

            <ButtonGradientPrimary
                size={'md'}
                onClick={onCreateCycle}
                className={`flex items-center self-start ${cycleCreateButtonHeight}`}
                dataTestId={'create-cycle-button'}
                leftIcon={
                    <Icon
                        name={'essential-add-circle'}
                        color={Colors.WHITE}
                        className={'mr-2'}
                        size={FIXED_ICON_SIZES.md}
                    />
                }
            >
                {t('cycleEvaluationDashboard.evaluationCycles.createTemplate')}
            </ButtonGradientPrimary>
        </div>
    );
};
